// Our own tracker gateway (replacing Traccar in stages). Stage 1 needs 7 clean nights in a row
// in the comparison before the gateway may answer the trackers itself.
import { useCallback, useEffect, useState } from 'react';
import { Link } from '../components/Links';
import { gateway, GatewayMetrics, GatewayOverview, gatewayStatus, GatewayUnit } from '../api';
import { GatewayStatus } from '../components/GatewayStatus';
import { useAuth } from '../auth';
import { ago, dateTime } from '../format';

const UnitCell = ({ unit, imei }: { unit: GatewayUnit | null; imei: string | null }) => (unit
  ? <Link to={unit.vehicle ? `/vehicles/${unit.vehicle.id}` : `/trackers/${unit.trackerId}`}>{unit.vehicle?.name ?? unit.imei}</Link>
  : <span className="mono">{imei ?? '–'}</span>);

const FLAG_LABEL: Record<string, string> = { duplicate: 'two connections for one IMEI', 'new-network': 'new network', 'decode-error': 'could not decode' };

export function Gateway() {
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const [data, setData] = useState<GatewayOverview | null>(null);
  const [metrics, setMetrics] = useState<GatewayMetrics | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const load = useCallback(() => {
    gateway.overview().then(setData).catch((e) => setError(e.message));
    gatewayStatus.metrics().then(setMetrics).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setError(''); setNotice('');
    try { await fn(); setNotice(msg); setPicked(new Set()); load(); } catch (e) { setError((e as Error).message); }
  };
  if (!data) return <div className="page">{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;

  // Clean nights in a row, newest first.
  let streak = 0;
  for (const r of data.runs) { if (!r.clean) break; streak++; }
  const toggle = (id: number) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="page">
      <div className="page-head"><h1>Gateway</h1></div>
      <p className="muted small">Our own tracker server, replacing Traccar in stages. In shadow mode it relays everything to Traccar and checks every night that it decoded exactly the same records. 7 clean nights in a row are needed before it answers the trackers itself.</p>
      {notice && <p className="notice" role="status">{notice}</p>}
      {error && <p className="error" role="alert">{error}</p>}

      {metrics && <GatewayStatus m={metrics} />}

      <section className="card">
        <h2>Nightly comparison <span className="muted small">· {streak} clean night{streak === 1 ? '' : 's'} in a row</span></h2>
        {data.runs.length === 0 ? <p className="muted">No comparison yet; the first runs at 03:30 UTC after the gateway has seen a day of data.</p> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Day</th><th>Gateway</th><th>Traccar</th><th>Matched</th><th>Only gateway</th><th>Only Traccar</th><th>Different</th></tr></thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={r.day} className={r.clean ? '' : 'gw-bad'} title={r.examples.map((e) => `${e.kind} ${e.fixTime}${e.fields ? ' ' + e.fields.join(', ') : ''}`).join('\n')}>
                    <td>{r.day} {r.clean ? <span className="chip sub-active">clean</span> : <span className="chip sub-expired">check</span>}</td>
                    <td>{r.ours}</td><td>{r.traccar}</td><td>{r.matched}</td><td>{r.onlyOurs}</td><td>{r.onlyTraccar}</td><td>{r.different}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Connected now <span className="muted small">· {data.live.length}</span></h2>
        {data.live.length === 0 ? <p className="muted">No trackers connected through the gateway.</p> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Unit</th><th>Mode</th><th>Address</th><th>Since</th><th>Records</th><th /></tr></thead>
              <tbody>
                {data.live.map((s) => (
                  <tr key={s.id}>
                    <td><UnitCell unit={s.unit} imei={s.imei} /></td><td>{s.mode}</td><td className="mono small">{s.ip}</td>
                    <td className="small">{ago(s.startedAt)}</td><td>{s.records}</td>
                    <td className="small">{s.flags.map((f) => <span key={f} className="chip sub-expired">{FLAG_LABEL[f] ?? f}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Held back <span className="muted small">· {data.quarantine.length}</span></h2>
        <p className="muted small">Records that can't be real or came in on a suspicious connection. They are kept out of journeys and the customer app until released.</p>
        {data.quarantine.length > 0 && (
          <>
            {admin && (
              <div className="actions">
                <button disabled={!picked.size} onClick={() => void act(() => gateway.decide([...picked], 'released'), 'Released; they appear in the data within a minute.')}>Release {picked.size || ''}</button>
                <button className="link danger" disabled={!picked.size} onClick={() => { if (window.confirm(`Delete ${picked.size} held-back record(s)?`)) void act(() => gateway.decide([...picked], 'deleted'), 'Deleted.'); }}>Delete</button>
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead><tr>{admin && <th />}<th>Unit</th><th>Recorded</th><th>Where</th><th>Why</th></tr></thead>
                <tbody>
                  {data.quarantine.map((q) => (
                    <tr key={q.id}>
                      {admin && <td><input type="checkbox" checked={picked.has(q.id)} onChange={() => toggle(q.id)} aria-label="Pick" /></td>}
                      <td><UnitCell unit={q.unit} imei={null} /></td>
                      <td className="small">{dateTime(q.fixTime)}</td>
                      <td className="small mono">{q.lat.toFixed(5)}, {q.lon.toFixed(5)}</td>
                      <td className="small">{q.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="card">
        <h2>Refused and blocked</h2>
        <p className="muted small">{data.refused24h.n} refused attempt{data.refused24h.n === 1 ? '' : 's'} from {data.refused24h.ips} address{data.refused24h.ips === 1 ? '' : 'es'} in the last 24 hours. 5 bad tries in 10 minutes block an address for an hour (a day if it happens again), unless one of our own trackers uses that address.</p>
        {data.blocks.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Blocked address</th><th>Until</th><th>Times</th><th>Last reason</th><th /></tr></thead>
              <tbody>
                {data.blocks.map((b) => (
                  <tr key={b.ip}>
                    <td className="mono small">{b.ip}</td><td className="small">{dateTime(b.until)}</td><td>{b.times}</td><td className="small">{b.reason}</td>
                    <td>{admin && <button className="link" onClick={() => void act(() => gateway.unblock(b.ip), `${b.ip} unblocked.`)}>Unblock</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.refusals.length > 0 && (
          <details>
            <summary className="small">Latest refused attempts ({data.refusals.length})</summary>
            <div className="table-wrap">
              <table>
                <thead><tr><th>When</th><th>Address</th><th>IMEI</th><th>Why</th></tr></thead>
                <tbody>
                  {data.refusals.map((r, i) => (
                    <tr key={i}><td className="small">{dateTime(r.at)}</td><td className="mono small">{r.ip}</td><td className="mono small">{r.imei ?? '–'}</td><td className="small">{r.reason}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </section>

      {data.flagged.length > 0 && (
        <section className="card">
          <h2>Flagged connections <span className="muted small">· last 7 days</span></h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Unit</th><th>Address</th><th>Started</th><th>Ended</th><th>Flags</th></tr></thead>
              <tbody>
                {data.flagged.map((s) => (
                  <tr key={s.id}>
                    <td><UnitCell unit={s.unit} imei={s.imei} /></td><td className="mono small">{s.ip}</td><td className="small">{dateTime(s.startedAt)}</td>
                    <td className="small">{s.endedAt ? `${dateTime(s.endedAt)} (${s.endReason})` : 'still open'}</td>
                    <td className="small">{s.flags.map((f) => FLAG_LABEL[f] ?? f).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
